// Portal tab — thin CRM-side summary of what's happening for this household
// in the client portal (bullogic-client-portal).
//
// Architecture note (Mar 2026 decision):
//   The portal admin (My Queue, Onboarding Pipeline, Broadcast, Notifications,
//   Module Catalog, View-As-Client, FSP authoring) lives INSIDE the portal
//   repo, not in BCC. Reasoning: the portal owns the module renderer and the
//   forthcoming FSP calculation engine. Staff edit views need to reuse those
//   components, so the admin has to be co-located with them.
//
// What BCC keeps:
//   - This tab: a small read-only summary so a BCC user has portal context
//     without leaving CRM
//   - Two deep-link buttons into the portal admin (open admin + view-as-client)
//
// What moved to the portal:
//   - Push module / mark complete / archive (now done in portal /admin)
//   - Notification composer + voice library
//   - Onboarding pipeline kanban + stuck-client list
//   - Bulk broadcasts
//
// The GET endpoint /api/data/portal-modules still backs this summary. The
// POST + PATCH endpoints on that route are now unused by BCC and will be
// retired once the portal admin is fully wired (see PORTAL_ADMIN_BRIEF.md).

import { useEffect, useState } from 'react';
import { apiFetch } from '../../../utils/apiFetch';
import { moduleLabel } from './portalModulesRegistry';

// Where the portal admin lives. Overridable via Vite env so dev/preview
// builds can point at a local portal instance. Defaults to production.
const PORTAL_BASE =
  import.meta.env?.VITE_PORTAL_BASE_URL || 'https://clients.bullogic.app';

const STATUS_LABELS = {
  pending:     { label: 'Pending',     cls: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'In Progress', cls: 'bg-bullogic-blue/10 text-bullogic-blue' },
  completed:   { label: 'Completed',   cls: 'bg-deep-teal/10 text-deep-teal' },
  archived:    { label: 'Archived',    cls: 'bg-gray-100 text-[#666]' },
};

const ROLE_LABELS = {
  primary: 'Primary',
  spouse:  'Spouse',
  member:  'Member',
};

function formatRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diffMs = Date.now() - then;
  const days = Math.floor(diffMs / 86400000);
  if (days < 1)   return 'today';
  if (days < 2)   return 'yesterday';
  if (days < 30)  return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

export default function PortalTab({ household }) {
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/data/portal-modules?household_id=${encodeURIComponent(household.id)}`);
        if (!res.ok) throw new Error(`Failed to load portal data (${res.status})`);
        const json = await res.json();
        if (cancelled) return;
        setAssignments(json.assignments || []);
        setUsers(json.users || []);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [household.id]);

  const adminUrl  = `${PORTAL_BASE}/admin/households/${encodeURIComponent(household.id)}`;
  const viewAsUrl = `${PORTAL_BASE}/admin/view-as/${encodeURIComponent(household.id)}`;

  const activeCount = assignments.filter(a => a.status === 'pending' || a.status === 'in_progress').length;
  const completedCount = assignments.filter(a => a.status === 'completed').length;

  return (
    <div className="space-y-4">
      {/* Deep-link buttons — the primary action on this tab. Everything else
          here is just context so the BCC user knows what to expect when they
          jump into the portal. */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h2 className="text-[15px] font-bold text-navy">Portal Admin</h2>
            <p className="text-[11px] text-[#888] mt-0.5">
              Push modules, edit, notify, and view-as-client all live in the portal admin.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={adminUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white text-[12px] font-bold rounded-[3px] hover:bg-navy/90"
          >
            Open in Portal Admin
            <span className="text-[10px] opacity-70">↗</span>
          </a>
          <a
            href={viewAsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-bullogic-blue text-bullogic-blue text-[12px] font-bold rounded-[3px] hover:bg-bullogic-blue/5"
          >
            View as Client
            <span className="text-[10px] opacity-70">↗</span>
          </a>
        </div>
      </div>

      {/* Portal users summary */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-[13px] font-bold text-navy">Portal Users</h3>
            <p className="text-[11px] text-[#888] mt-0.5">
              {loading
                ? 'Loading...'
                : users.length === 0
                  ? 'No one in this household has portal access yet.'
                  : `${users.length} user${users.length === 1 ? '' : 's'} with access to clients.bullogic.app`}
            </p>
          </div>
        </div>
        {!loading && users.length > 0 && (
          <div className="space-y-1.5">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between py-1.5 border-t border-gray-100 first:border-t-0">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[3px] bg-bullogic-blue/10 text-bullogic-blue">
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-bold text-navy truncate">
                      {u.displayName || u.email}
                    </div>
                    <div className="text-[10px] text-[#888] truncate">{u.email}</div>
                  </div>
                </div>
                <div className="text-[10px] text-[#888] text-right whitespace-nowrap">
                  {u.lastSeenAt
                    ? <>Last seen <span className="text-navy font-bold">{formatRelative(u.lastSeenAt)}</span></>
                    : u.firstLoginAt
                      ? <>Logged in <span className="text-navy font-bold">{formatRelative(u.firstLoginAt)}</span></>
                      : <span className="text-amber-700">Invite pending</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Module assignments summary — read-only here. Actions live in portal admin. */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-[13px] font-bold text-navy">Module Activity</h3>
            <p className="text-[11px] text-[#888] mt-0.5">
              {loading
                ? 'Loading...'
                : `${activeCount} active · ${completedCount} completed`}
            </p>
          </div>
        </div>

        {error ? (
          <div className="text-[12px] text-rose py-2">{error}</div>
        ) : !loading && assignments.length === 0 ? (
          <div className="text-[11px] text-[#888] italic py-2">
            No modules pushed to this household yet. Use Portal Admin to push one.
          </div>
        ) : (
          <div className="space-y-1.5">
            {assignments.slice(0, 6).map(a => {
              const statusInfo = STATUS_LABELS[a.status] || { label: a.status, cls: 'bg-gray-100 text-[#666]' };
              return (
                <div key={a.id} className="flex items-center justify-between py-1.5 border-t border-gray-100 first:border-t-0">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-[12px] font-bold text-navy truncate">{moduleLabel(a.moduleKey)}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[3px] ${statusInfo.cls}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <div className="text-[10px] text-[#888] whitespace-nowrap">
                    {a.latestResponseAt
                      ? <>Submitted <span className="text-deep-teal font-bold">{formatRelative(a.latestResponseAt)}</span></>
                      : <>Pushed {formatRelative(a.assignedAt)}</>}
                  </div>
                </div>
              );
            })}
            {assignments.length > 6 && (
              <div className="text-[10px] text-[#888] italic pt-1">
                +{assignments.length - 6} more — see Portal Admin for the full list.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
